"use client"

import { useState, FormEvent, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Mail, HelpCircle, CreditCard, Zap, Headphones, Settings, Users, FileQuestion, Loader2 } from 'lucide-react';
import { getAuthInstance } from '@/firebase';
import { onAuthStateChanged, Auth, User } from "firebase/auth";

export default function ContactPage() {
  const params = useParams();
  const locale = params?.locale as string || 'sv';
  const t = useTranslations('contact');
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  const [formState, setFormState] = useState({
    category: 'technical',
    name: '',
    email: '',
    company: '',
    website: '',
    priority: 'medium',
    subject: '',
    message: '',
    attachmentUrl: null,
    privacyConsent: false
  });

  // Initialize auth and set user data if available
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const initAuth = async () => {
      try {
        const authInstance = await getAuthInstance();
        
        unsubscribe = onAuthStateChanged(authInstance, (user) => {
          setCurrentUser(user);
          if (user) {
            setFormState(prev => ({
              ...prev,
              name: user.displayName || '',
              email: user.email || ''
            }));
          }
        });
        
    return () => {
        if (unsubscribe) unsubscribe();
    };      } catch (error) {
        console.error('Error initializing auth:', error);
      }
    };
    
    initAuth();
  }, []);

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFormState(prev => ({ ...prev, category: value }));
  };

  const handlePriorityChange = (value: string) => {
    setFormState(prev => ({ ...prev, priority: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formState.name || !formState.email || !formState.subject || !formState.message) {
      toast({
        title: t('validation.missingInfo'),
        description: t('validation.fillRequired'),
        variant: "destructive"
      });
      return;
    }

    if (!formState.privacyConsent) {
      toast({
        title: t('validation.privacyRequired'),
        description: t('validation.privacyMessage'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // If user is authenticated, we'll send the token in the request
      let token = null;
      if (currentUser) {
        token = await currentUser.getIdToken();
      }

      const response = await fetch('/api/support-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(formState)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('error.sendFailed'));
      }

      toast({
        title: t('success.title'),
        description: t('success.description')
      });

      // Reset form
      setFormState({
        category: 'technical',
        name: currentUser?.displayName || '',
        email: currentUser?.email || '',
        company: '',
        website: '',
        priority: 'medium',
        subject: '',
        message: '',
        attachmentUrl: null,
        privacyConsent: false
      });
    } catch (error: any) {
      toast({
        title: t('error.title'),
        description: error.message || t('error.generalError'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center max-w-[800px] mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 w-fit mx-auto mb-6">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-600 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              {t('hero.badge')}
            </div>
            
            {/* Hero heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('hero.title').split(t('hero.titleHighlight'))[0]}
              <span className="text-blue-600">{t('hero.titleHighlight')}</span>
              {t('hero.title').split(t('hero.titleHighlight'))[1]}
            </h1>
            
            {/* Brief description */}
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-[600px] mx-auto">
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('contactInfo.title')}</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('contactInfo.description')}
                </p>
              </div>
              
              <div className="space-y-4">
                <Card className="border border-gray-200 dark:border-gray-800">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-4">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                          <Mail className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">{t('contactInfo.email.title')}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {t('contactInfo.email.description')}
                        </p>
                        <a 
                          href={`mailto:${t('contactInfo.email.address')}`}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-sm mt-2 inline-block"
                        >
                          {t('contactInfo.email.address')}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div>
                <div className="flex items-center mb-4">
                  <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="font-medium text-gray-900 dark:text-white">{t('contactInfo.help.title')}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('contactInfo.help.description')}
                </p>
                <Link 
                  href={`/${locale}/help`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center"
                >
                  {t('contactInfo.help.link')} <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('form.title')}
                </h2>
                
                <form className="space-y-6" onSubmit={handleSubmit}>
                  {/* Contact Category */}
                  <div className="space-y-4">
                    <Label className="text-base">{t('form.category.label')}</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <RadioGroup 
                          value={formState.category} 
                          onValueChange={handleCategoryChange} 
                          className="space-y-3"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="technical" id="technical" />
                            <Label htmlFor="technical" className="flex items-center cursor-pointer">
                              <Settings className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.technical')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="billing" id="billing" />
                            <Label htmlFor="billing" className="flex items-center cursor-pointer">
                              <CreditCard className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.billing')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="feature" id="feature" />
                            <Label htmlFor="feature" className="flex items-center cursor-pointer">
                              <Zap className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.feature')}
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div>
                        <RadioGroup 
                          value={formState.category} 
                          onValueChange={handleCategoryChange} 
                          className="space-y-3"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="sales" id="sales" />
                            <Label htmlFor="sales" className="flex items-center cursor-pointer">
                              <Headphones className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.sales')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="partnership" id="partnership" />
                            <Label htmlFor="partnership" className="flex items-center cursor-pointer">
                              <Users className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.partnership')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="other" id="other" />
                            <Label htmlFor="other" className="flex items-center cursor-pointer">
                              <FileQuestion className="h-4 w-4 mr-2 text-gray-500" />
                              {t('form.category.other')}
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('form.fields.nameRequired')}</Label>
                      <Input 
                        id="name"
                        name="name"
                        value={formState.name}
                        onChange={handleInputChange}
                        placeholder={t('form.fields.namePlaceholder')}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('form.fields.emailRequired')}</Label>
                      <Input 
                        id="email"
                        name="email"
                        value={formState.email}
                        onChange={handleInputChange}
                        type="email" 
                        placeholder={t('form.fields.emailPlaceholder')}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('form.fields.company')}</Label>
                      <Input 
                        id="company"
                        name="company"
                        value={formState.company}
                        onChange={handleInputChange}
                        placeholder={t('form.fields.companyPlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">{t('form.fields.website')}</Label>
                      <Input 
                        id="website"
                        name="website"
                        value={formState.website}
                        onChange={handleInputChange}
                        placeholder={t('form.fields.websitePlaceholder')}
                      />
                    </div>
                  </div>
                  
                  {/* Priority Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="priority">{t('form.fields.priority')}</Label>
                    <Select 
                      value={formState.priority} 
                      onValueChange={handlePriorityChange}
                    >
                      <SelectTrigger id="priority">
                        <SelectValue placeholder={t('form.fields.priorityPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('form.priority.low')}</SelectItem>
                        <SelectItem value="medium">{t('form.priority.medium')}</SelectItem>
                        <SelectItem value="high">{t('form.priority.high')}</SelectItem>
                        <SelectItem value="urgent">{t('form.priority.urgent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('form.fields.subject')}</Label>
                    <Input 
                      id="subject"
                      name="subject"
                      value={formState.subject}
                      onChange={handleInputChange}
                      placeholder={t('form.fields.subjectPlaceholder')}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('form.fields.message')}</Label>
                    <Textarea 
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleInputChange}
                      placeholder={t('form.fields.messagePlaceholder')}
                      className="min-h-[150px]"
                      required
                    />
                  </div>
                  
                  {/* Attachment option */}
                  <div className="space-y-2">
                    <Label htmlFor="attachment">{t('form.fields.attachment')}</Label>
                    <Input 
                      id="attachment" 
                      type="file" 
                      className="cursor-pointer" 
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('form.fields.attachmentInfo')}
                    </p>
                  </div>
                  
                  <div className="flex items-start space-x-2 mt-4">
                    <div className="flex items-center h-5">
                      <input
                        id="privacyConsent"
                        name="privacyConsent"
                        type="checkbox"
                        checked={formState.privacyConsent}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div className="text-sm">
                      <label htmlFor="privacyConsent" className="text-gray-500 dark:text-gray-400">
                        {t('form.privacy.text')} <Link href={`/${locale}/privacy-policy`} className="text-blue-600 hover:underline">{t('form.privacy.link')}</Link> {t('form.privacy.consent')}
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <Button 
                      type="submit" 
                      className="w-full md:w-auto bg-blue-600 hover:bg-blue-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('form.submit.submitting')}
                        </>
                      ) : (
                        t('form.submit.button')
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {t('form.submit.responseTime')}
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}